import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as PluginService from "../../services/Plugin";

// Initial state
const initialState = {
  plugins: [],
  loading: false,
  error: null,
  actionLoading: false,
  actionError: null,
};

// Async thunks
export const fetchPlugins = createAsyncThunk(
  "plugins/fetchPlugins",
  async (siteId, { rejectWithValue }) => {
    try {
      const response = await PluginService.getPlugins(siteId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch plugins",
      );
    }
  },
  {
    condition: (_, { getState }) => {
      const { plugins } = getState();
      return !plugins.loading;
    },
  },
);

export const installPlugin = createAsyncThunk(
  "plugins/installPlugin",
  async ({ siteId, plugin, activate }, { rejectWithValue }) => {
    try {
      const response = await PluginService.installPlugin(
        siteId,
        plugin,
        activate,
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to install plugin",
      );
    }
  },
);

export const activatePlugin = createAsyncThunk(
  "plugins/activatePlugin",
  async ({ siteId, plugin }, { rejectWithValue }) => {
    try {
      const response = await PluginService.activatePlugin(siteId, plugin);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to activate plugin",
      );
    }
  },
);

export const deactivatePlugin = createAsyncThunk(
  "plugins/deactivatePlugin",
  async ({ siteId, plugin }, { rejectWithValue }) => {
    try {
      const response = await PluginService.deactivatePlugin(siteId, plugin);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to deactivate plugin",
      );
    }
  },
);

export const deletePlugin = createAsyncThunk(
  "plugins/deletePlugin",
  async ({ siteId, plugin }, { rejectWithValue }) => {
    try {
      const response = await PluginService.deletePlugin(siteId, plugin);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to delete plugin",
      );
    }
  },
);

export const updatePlugin = createAsyncThunk(
  "plugins/updatePlugin",
  async ({ siteId, plugin }, { rejectWithValue }) => {
    try {
      const response = await PluginService.updatePlugin(siteId, plugin);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to update plugin",
      );
    }
  },
);

// Slice
const pluginsSlice = createSlice({
  name: "plugins",
  initialState,
  reducers: {
    clearPluginsState: (state) => {
      state.plugins = [];
      state.loading = false;
      state.error = null;
      state.actionLoading = false;
      state.actionError = null;
    },
    clearActionError: (state) => {
      state.actionError = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch plugins
    builder
      .addCase(fetchPlugins.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlugins.fulfilled, (state, action) => {
        state.loading = false;
        state.plugins = action.payload.data?.plugins || [];
        state.error = null;
      })
      .addCase(fetchPlugins.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Install plugin
    builder
      .addCase(installPlugin.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(installPlugin.fulfilled, (state) => {
        state.actionLoading = false;
        state.actionError = null;
      })
      .addCase(installPlugin.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });

    // Activate plugin
    builder
      .addCase(activatePlugin.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(activatePlugin.fulfilled, (state) => {
        state.actionLoading = false;
        state.actionError = null;
      })
      .addCase(activatePlugin.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });

    // Deactivate plugin
    builder
      .addCase(deactivatePlugin.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(deactivatePlugin.fulfilled, (state) => {
        state.actionLoading = false;
        state.actionError = null;
      })
      .addCase(deactivatePlugin.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });

    // Delete plugin
    builder
      .addCase(deletePlugin.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(deletePlugin.fulfilled, (state) => {
        state.actionLoading = false;
        state.actionError = null;
      })
      .addCase(deletePlugin.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });

    // Update plugin
    builder
      .addCase(updatePlugin.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(updatePlugin.fulfilled, (state) => {
        state.actionLoading = false;
        state.actionError = null;
      })
      .addCase(updatePlugin.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });
  },
});

export const { clearPluginsState, clearActionError } = pluginsSlice.actions;

export default pluginsSlice.reducer;
